import { DateFormatter, DueDateFormatter, Translate } from '@components'

import { TableColumn, Text } from '@billy/design-system'
import { useMemo } from 'react'
import { Row } from 'react-table'

import { SalesReportTableFormattedCurrency } from '../../elements/SalesReportTableFormattedCurrency'
import { SalesOverviewReportTableAccessor } from '../../enums/salesOverviewReportTableAccessor'
import { SalesOverviewReportTableRow } from '../../types/salesOverviewReportTableRow'
import { useSalesReportFilters } from '../useSalesReportFilters'
import { useTotalSales } from '../useTotalSales'
import { useSalesOverviewReport } from './useSalesOverviewReport'

export const useSalesOverviewReportTableColumns = () => {
  const [{ shouldShowVat }] = useSalesReportFilters()
  const { salesOverviewReport } = useSalesOverviewReport()
  const { grossTotalSales, netTotalSales } = useTotalSales(salesOverviewReport)

  const baseColumns: TableColumn<SalesOverviewReportTableRow>[] = useMemo(
    () => [
      {
        accessor: SalesOverviewReportTableAccessor.InvoiceNumber,
        Cell: ({ value: invoiceNumber }: { value: number }) => (invoiceNumber ? invoiceNumber.toString() : ''),
        Footer: <Translate value="sales_report.sales_overview.table.footer.total_sales" />,
        Header: <Translate value="sales_report.sales_overview.table.column.invoice" />,
        size: 'xs',
        truncate: true,
        width: 100,
      },
      {
        accessor: SalesOverviewReportTableAccessor.IssueDate,
        Cell: ({ value: issueDate }: { value: string }) => (issueDate ? <DateFormatter value={issueDate} /> : null),
        fixedSize: true,
        Header: <Translate value="sales_report.sales_overview.table.column.issue_date" />,
        size: 's',
      },
      {
        accessor: SalesOverviewReportTableAccessor.CustomerName,
        Cell: ({ value: customerName }: { value: string }) => customerName,
        Header: <Translate value="sales_report.sales_overview.table.column.customer" />,
        minWidth: 60,
        size: 'm',
        truncate: true,
      },
      {
        accessor: SalesOverviewReportTableAccessor.PaymentDate,
        Cell: ({ value: paidDate, row }: { value?: string | null; row: Row<SalesOverviewReportTableRow> }) => {
          if (!paidDate) {
            return <Text>-</Text>
          }

          return row.original.isPaid ? <DateFormatter value={paidDate} /> : <DueDateFormatter value={paidDate} />
        },
        fixedSize: true,
        minWidth: 40,
        Header: <Translate value="sales_report.sales_overview.table.column.payment_date" />,
        size: 's',
      },
      {
        accessor: SalesOverviewReportTableAccessor.TaxTotal,
        alignment: 'right',
        Cell: ({ value }) => <SalesReportTableFormattedCurrency value={value} />,
        Header: <Translate value="sales_report.sales_overview.table.column.vat_total" />,
        hidden: !shouldShowVat,
        size: 's',
      },
      {
        accessor: SalesOverviewReportTableAccessor.NetTotal,
        alignment: 'right',
        Cell: ({ value }) => <SalesReportTableFormattedCurrency value={value} />,
        Footer: netTotalSales,
        Header: <Translate value="sales_report.sales_overview.table.column.net_total" />,
        size: 's',
      },
      {
        accessor: SalesOverviewReportTableAccessor.GrossTotal,
        alignment: 'right',
        Cell: ({ value }) => <SalesReportTableFormattedCurrency value={value} />,
        Footer: grossTotalSales,
        Header: <Translate value="sales_report.sales_overview.table.column.gross_total" />,
        hidden: !shouldShowVat,
        size: 's',
      },
    ],
    [grossTotalSales, netTotalSales, shouldShowVat],
  )

  return baseColumns.filter((column) => !column.hidden)
}
